import { Injectable, computed, effect, signal } from '@angular/core';
import { SupportedLocale, TRANSLATIONS } from '../utils/i18n.translations';

const STORAGE_KEY = 'ccapp.bo.locale';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly localeState = signal<SupportedLocale>(this.loadInitialLocale());

  readonly locale = computed(() => this.localeState());
  readonly languageOptions: Array<{ code: SupportedLocale; label: string }> = [
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
  ];

  constructor() {
    effect(() => {
      const locale = this.localeState();
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, locale);
      }
      if (typeof document !== 'undefined') {
        document.documentElement.lang = locale;
      }
    });
  }

  setLocale(locale: SupportedLocale): void {
    this.localeState.set(locale);
  }

  t(key: string, params?: Record<string, string | number | null | undefined>): string {
    const locale = this.localeState();
    const dictionary = TRANSLATIONS[locale] ?? {};
    let value = dictionary[key] ?? key;

    if (!params) {
      return value;
    }

    Object.entries(params).forEach(([name, rawValue]) => {
      const replacement = rawValue == null ? '' : String(rawValue);
      value = value.replaceAll(`{{${name}}}`, replacement);
    });
    return value;
  }

  private loadInitialLocale(): SupportedLocale {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'fr' || stored === 'en') {
        return stored;
      }

      const browserLocale = window.navigator.language.toLowerCase();
      if (browserLocale.startsWith('fr')) {
        return 'fr';
      }
    }
    return 'en';
  }
}
